import { useState, useRef, useEffect } from 'react';
import { Street } from '../pages/PlaygroundPage/PlaygroundPage';
import { HandSummaryFull } from '../pages/HandReplayerPage';

interface HandSummaryStreet {
  potSizeAtStart: number;
  potSizeAtEnd: number;
  cardsDealt: string[] | null;
  actions: Array<{
    playerId: number;
    type: string;
    amount: number | null;
  }>;
}

interface Player {
  id: number;
  name: string;
  isHero: boolean;
  position: string;
  initialStack: number;
}

interface Analysis {
  streets?: Record<Street, { analysis?: string }>;
}

interface UseAutoPlayProps {
  activeHandData: HandSummaryFull | null;
  currentStreetKey: Street;
  currentActionIndex: number;
  currentStreetData: HandSummaryStreet | null;
  streetsOrder: Street[];
  allPlayersInHand: Player[];
  loadedAnalysis: Analysis | null;
  onActionIndexChange: (index: number) => void;
  onStreetChange: (street: Street) => void;
  onCoachingChange: (text: string | null, visible: boolean) => void;
  onLastActionChange: (playerId: number | null, type: string | null, amount: number | null) => void;
  onPlayerContributionsChange: (contributions: Record<number, number>) => void;
}

export const useAutoPlay = ({
  activeHandData,
  currentStreetKey,
  currentActionIndex,
  currentStreetData,
  streetsOrder,
  allPlayersInHand,
  loadedAnalysis,
  onActionIndexChange,
  onStreetChange,
  onCoachingChange,
  onLastActionChange,
  onPlayerContributionsChange
}: UseAutoPlayProps) => {
  const [isAutoPlaying, setIsAutoPlaying] = useState<boolean>(false);
  const [autoPlaySpeed, setAutoPlaySpeed] = useState<number>(1500);
  const [playToNextCoaching, setPlayToNextCoaching] = useState<boolean>(false);
  const autoPlayIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Checks if there's coaching available for a given street and action
   */
  const hasCoachingAtPosition = (streetKey: Street, actionIndex: number): boolean => {
    if (!loadedAnalysis || !loadedAnalysis.streets || !loadedAnalysis.streets[streetKey]) {
      return false;
    }
    
    const streetData = activeHandData?.[streetKey];
    if (!streetData || actionIndex < 0 || actionIndex >= streetData.actions.length) return false;
    
    const actionAtIndex = streetData.actions[actionIndex];
    const actionPlayer = allPlayersInHand.find(p => p.id === actionAtIndex.playerId);
    const isHeroAction = actionPlayer?.isHero === true;
    
    return isHeroAction && !!loadedAnalysis.streets[streetKey].analysis;
  };

  /**
   * Finds the next coaching spot in the hand
   */
  const findNextCoachingSpot = (): boolean => {
    let foundCoaching = false;
    
    for (const street of streetsOrder) {
      const streetIndex = streetsOrder.indexOf(street);
      const currentStreetIndex = streetsOrder.indexOf(currentStreetKey);
      
      if (streetIndex < currentStreetIndex) continue;
      
      const streetData = activeHandData?.[street];
      if (!streetData) continue;
      
      if (!loadedAnalysis?.streets?.[street]?.analysis) continue;
      
      const startIndex = streetIndex === currentStreetIndex ? currentActionIndex + 1 : 0;
      
      for (let actionIndex = startIndex; actionIndex < streetData.actions.length; actionIndex++) {
        const action = streetData.actions[actionIndex];
        const actionPlayer = allPlayersInHand.find(p => p.id === action.playerId);
        
        if (actionPlayer?.isHero === true) {
          foundCoaching = true;
          break;
        }
      }
      
      if (foundCoaching) break;
    }
    
    return foundCoaching;
  };

  /**
   * Checks if we're at the end of the hand
   */
  const isAtEndOfHand = (): boolean => {
    const currentStreetNum = streetsOrder.indexOf(currentStreetKey);
    const isLastStreet = currentStreetNum === streetsOrder.length - 1;
    const isLastActionInStreet = currentStreetData && currentActionIndex >= currentStreetData.actions.length - 1;
    return isLastStreet && !!isLastActionInStreet;
  };

  /**
   * Handles play/pause functionality
   */
  const handlePlayPause = () => {
    setIsAutoPlaying(!isAutoPlaying);
    setPlayToNextCoaching(false); 
    if (!isAutoPlaying) {
      onCoachingChange(null, false);
    }
  };

  /**
   * Handles play to next coaching functionality
   */
  const handlePlayToNextCoaching = () => {
    if (findNextCoachingSpot()) {
      setPlayToNextCoaching(true);
      setIsAutoPlaying(true);
      onCoachingChange(null, false);
      console.log("Playing to next hero action with coaching...");
    } else {
      console.log("No more hero actions with coaching found ahead");
    }
  };

  /**
   * Handles speed change
   */
  const handleSpeedChange = (newSpeed: number) => {
    setAutoPlaySpeed(newSpeed);
  };

  /**
   * Resets to start of hand
   */
  const handleResetToStart = () => {
    setIsAutoPlaying(false);
    setPlayToNextCoaching(false);
    onStreetChange('preflop');
    onActionIndexChange(-1);
    onCoachingChange(null, false);
    onLastActionChange(null, null, null);
    console.log("Reset to start of hand");
  };

  // Auto-play effect
  useEffect(() => {
    if (isAutoPlaying) {
      autoPlayIntervalRef.current = setInterval(() => {
        if (isAtEndOfHand()) {
          setIsAutoPlaying(false);
          setPlayToNextCoaching(false);
          console.log("Auto-play completed - reached end of hand");
        } else {
          // Advance to next action
          if (!currentStreetData) return;
          
          if (currentActionIndex < currentStreetData.actions.length - 1) {
            const nextIndex = currentActionIndex + 1;
            onActionIndexChange(nextIndex);
            onCoachingChange(null, false);
            
            // Check if we should stop for coaching
            if (playToNextCoaching) {
              const nextAction = currentStreetData.actions[nextIndex];
              const nextActionPlayer = allPlayersInHand.find(p => p.id === nextAction.playerId);
              
              if (nextActionPlayer?.isHero === true && loadedAnalysis?.streets?.[currentStreetKey]?.analysis) {
                setTimeout(() => {
                  setIsAutoPlaying(false);
                  setPlayToNextCoaching(false);
                  console.log("Paused at hero action for coaching");
                }, 100);
              }
            }
          } else {
            // Move to next street
            const currentStreetNum = streetsOrder.indexOf(currentStreetKey);
            if (currentStreetNum < streetsOrder.length - 1) {
              const nextStreetKey = streetsOrder[currentStreetNum + 1];
              onStreetChange(nextStreetKey);
              onActionIndexChange(-1);
              onLastActionChange(null, null, null);
              
              // Reset player contributions for new street
              let emptyContributions: Record<number, number> = {};
              allPlayersInHand.forEach(p => { emptyContributions[p.id] = 0; });
              onPlayerContributionsChange(emptyContributions);
              
              // Check for coaching on new street
              if (playToNextCoaching) {
                const nextStreetData = activeHandData?.[nextStreetKey];
                if (nextStreetData && nextStreetData.actions.length > 0) {
                  const firstActionOnNextStreet = nextStreetData.actions[0];
                  const firstActionPlayer = allPlayersInHand.find(p => p.id === firstActionOnNextStreet.playerId);
                  
                  if (firstActionPlayer?.isHero === true && loadedAnalysis?.streets?.[nextStreetKey]?.analysis) {
                    setTimeout(() => {
                      setIsAutoPlaying(false);
                      setPlayToNextCoaching(false);
                      console.log("Paused at hero action for coaching (new street)");
                    }, 100);
                  }
                }
              }
            }
          }
        }
      }, autoPlaySpeed);
    }

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
        autoPlayIntervalRef.current = null;
      }
    };
  }, [
    isAutoPlaying, 
    autoPlaySpeed, 
    currentStreetKey, 
    currentActionIndex, 
    currentStreetData, 
    streetsOrder, 
    allPlayersInHand, 
    playToNextCoaching, 
    loadedAnalysis, 
    activeHandData,
    onActionIndexChange,
    onStreetChange,
    onCoachingChange,
    onLastActionChange,
    onPlayerContributionsChange
  ]);

  return {
    isAutoPlaying,
    autoPlaySpeed,
    playToNextCoaching,
    handlePlayPause,
    handlePlayToNextCoaching,
    handleSpeedChange,
    handleResetToStart,
    hasCoachingAtPosition
  };
};